import { useAdminContext } from '@/contexts/AdminContext';
import {
    MessageType, ToolCompleted, ToolStarted, ToolSummaryDelta
} from '@common/api/agentService';
import Icon from '@common/components/Icon';
import Tooltip from '@common/components/Tooltip';
import dayjs from 'dayjs';
import React from 'react';
import styles from './Tool.module.scss';

interface ToolsProps {
    message: MessageType;
}

type ToolConfig = {
    title: string;
    describe: (args: Record<string, any>) => string;
    icon: string;
};

const getQuery = (args: Record<string, any>) => {
    const q = args?.query || args?.keyword || args?.q || '';
    return typeof q === 'string' ? q : JSON.stringify(q);
}

export const TOOL_MAP: Record<string, ToolConfig> = {
    google_search: {
        title: 'Web search',
        describe: (args) => getQuery(args),
        icon: 'search',
    },
    amazon_product_search: {
        title: 'Amazon product search',
        describe: (args) => getQuery(args),
        icon: 'amazon',
    },
    google_trends_search: {
        title: 'Google trends',
        describe: (args) => {
            const q = getQuery(args);
            const region = args?.region ? ` · ${args.region}` : '';
            const period = args?.time_range || args?.period;
            const pr = period ? ` · ${period}` : '';
            return `${q}${region}${pr}`.trim();
        },
        icon: 'search',
    },
    ads_search: {
        title: 'Facebook ads library',
        describe: (args) => getQuery(args),
        icon: 'meta-ads',
    },
    facebook_ads_search: {
        title: 'Facebook ads library',
        describe: (args) => getQuery(args),
        icon: 'meta-ads',
    },
    web_scraper: {
        title: 'Web scraper',
        describe: (args) => args?.url || args?.target || JSON.stringify(args || {}),
        icon: 'search',
    },
    aliexpress_image_product_source: {
        title: 'AliExpress image search',
        describe: (args) => {
            const imgs = Array.isArray(args?.images) ? args.images.length : (args?.image ? 1 : 0);
            return imgs ? `${imgs} image${imgs > 1 ? 's' : ''}` : 'Image search';
        },
        icon: 'product_search',
    },
    aliexpress_text_product_search: {
        title: 'AliExpress text search',
        describe: (args) => getQuery(args),
        icon: 'product_search',
    },
    doc_to_markdown: {
        title: 'Parsing files',
        describe: (args) => `Parsing ${args?.file_url?.split('/').pop() || 'file'}`,
        icon: 'search',
    },
    get_orders: {
        title: 'Get orders',
        describe: (args) => `from ${dayjs(args?.start_date).format('YYYY MMM,DD')} to ${dayjs(args?.end_date).format('YYYY MMM,DD')}`,
        icon: 'shopify',
    },
    similarweb_analyse: {
        title: 'SimilarWeb analyse',
        describe: (args) => `Analyse ${args?.domain}`,
        icon: 'similarweb',
    },
    get_shop_info: {
        title: 'Get shop info',
        describe: (args) => JSON.stringify(args),
        icon: 'shopify',
    },
    get_products: {
        title: 'Get products',
        describe: (args) => JSON.stringify(args),
        icon: 'shopify',
    },
    get_customers: {
        title: 'Get customers',
        describe: (args) => JSON.stringify(args),
        icon: 'shopify',
    },
    shopify_shop_products: {
        title: 'Get products',
        describe: (args) => `from ${args?.shop_url}`,
        icon: 'shopify',
    },
    google_news: {
        title: 'Google news',
        describe: (args) => getQuery(args),
        icon: 'google',
    },
    plan: {
        title: 'Plan',
        describe: (args) => args.brief,
        icon: 'plan',
    },
    match: {
        title: 'Match',
        describe: (args) => args.brief,
        icon: 'match',
    },
    file: {
        title: 'File',
        describe: (args) => args.brief,
        icon: 'file',
    },
    shell: {
        title: 'Shell',
        describe: (args) => args.brief,
        icon: 'shell',
    },
    // dashboard agent
    kpi: {
        title: 'Calculate KPI',
        describe: (args) => args.brief,
        icon: 'database',
    },
    chart: {
        title: 'Generate chart',
        describe: (args) => args.brief,
        icon: 'dashboard',
    },
    table_info: {
        title: 'Get data info',
        describe: (args) => args.brief,
        icon: 'database',
    },
    sql: {
        title: 'Search data',
        describe: (args) => args.brief,
        icon: 'search',
    },
    // ask ai agent
    table_tool: {
        title: 'Get table info',
        describe: (args) => args.tool_summary,
        icon: 'database',
    },
    kpi_sql_tool: {
        title: 'Calculate KPI',
        describe: (args) => args.tool_summary,
        icon: 'database',
    },
};


const Started: React.FC<{message: ToolStarted}> = ({ message }) => {
    const {chat: {setCurrentResultId}} = useAdminContext();
    const toolName = message?.tool_name as string;
    const args = message?.input_args || {};
    const toolSummary = message.tool_summary;
    const cfg = TOOL_MAP[toolName] || {
        title: toolName,
        describe: (args) => JSON.stringify(args || {}),
        icon: 'search',
    };

    const onViewDetails = () => {
        setCurrentResultId(message.tool_run_id);
    }

    return (
        <Tooltip content={cfg?.describe(args) || cfg?.title + ' - ' + toolSummary}>
            <div className={styles.toolStartCard}>
                <div className={styles.toolStartLeft}>
                    <div className={styles.toolStartIcon}>
                        <Icon name={cfg?.icon} size={20} />
                    </div>
                    <div className={styles.toolStartText}>
                        <span className={styles.toolStartLabel}>
                            {cfg?.title}
                            {' - '}
                        </span>
                        <span className={styles.toolStartQuery}>
                            {cfg?.describe(args) || toolSummary}
                        </span>
                    </div>
                </div>
                {/* <div className={styles.toolStartRight}>
                <div
                    className={styles.toolStartButton}
                    onClick={onViewDetails}
                >
                    View details
                </div>
            </div> */}
            </div>
        </Tooltip>

    );
}

const Completed: React.FC<{message: ToolCompleted}> = ({ message }) => {
    return (
        <div className={styles.toolCompletedCard}>
            {message.output_summary}
        </div>
    );
}

const Summary: React.FC<{message: ToolSummaryDelta}> = ({ message }) => {
    return (
        <div className={styles.toolSummary}>
            {message.content}
        </div>
    )
}

export default {
    Started, Completed, Summary
};
